/**
 * Tracking Hook Factory
 *
 * Creates hooks for tracking entities (bills, committees) with consistent
 * optimistic updates, tier limits, and toggle functionality.
 *
 * This factory eliminates ~200 lines of duplicated code between
 * useTrackedBills and useTrackedCommittees.
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { POSTGRES_UNIQUE_VIOLATION, standardQueryDefaults } from './queryHelpers';

/**
 * Base tracked item shape - all tracked entities have these fields
 */
export interface BaseTrackedItem {
  id: string;
  user_id: string;
  tracked_at: string;
}

/**
 * Configuration for creating a tracking hook
 */
export interface TrackingConfig<
  TItem extends BaseTrackedItem,
  TEntityId extends number | string = number,
> {
  /** Supabase table name (e.g., 'user_tracked_bills', 'tracked_committees') */
  tableName: string;

  /** Query key prefix (e.g., 'tracked-bills', 'tracked-committees') */
  queryKey: string;

  /** Entity ID field name in the table (e.g., 'bill_id', 'committee_id') */
  entityIdField: string;

  /** Tier limits key from useAuth (e.g., 'trackedBills', 'trackedCommittees') */
  tierLimitKey: 'trackedBills' | 'trackedCommittees';

  /** Columns to select when fetching (excluding user_id which is always included) */
  selectColumns: string;

  /** Function to extract entity ID from tracked item */
  getEntityId: (item: TItem) => TEntityId;

  /** Entity display name for error messages (e.g., 'bill', 'committee') */
  entityName: string;
}

/**
 * Return type for the tracking hook
 */
export interface TrackingHookResult<
  TItem extends BaseTrackedItem,
  TEntityId extends number | string = number,
> {
  // Data
  trackedItems: TItem[];
  trackedIds: Set<TEntityId>;
  trackedCount: number;

  // Tier limits
  limit: number | null;
  canTrackMore: boolean;
  remainingSlots: number | null;

  // State
  isLoading: boolean;
  isError: boolean;
  error: Error | null;

  // Helpers
  isTracked: (entityId: TEntityId) => boolean;

  // Mutations (raw - for extension by specific hooks)
  trackMutation: ReturnType<typeof useMutation<TItem, Error, TEntityId>>;
  untrackMutation: ReturnType<typeof useMutation<void, Error, TEntityId>>;

  // Convenience
  toggleTracking: (entityId: TEntityId) => Promise<void>;
  isToggling: boolean;

  // Refetch
  refetch: () => void;
}

/**
 * Create a tracking hook for a specific entity type.
 *
 * @param config - Configuration for the tracking hook
 * @returns A hook function that manages tracking for the entity type
 *
 * @example
 * // In useTrackedCommittees.ts
 * const useBaseTracking = createTrackingHook<TrackedCommittee>({
 *   tableName: 'tracked_committees',
 *   queryKey: 'tracked-committees',
 *   entityIdField: 'committee_id',
 *   tierLimitKey: 'trackedCommittees',
 *   selectColumns: 'id, user_id, committee_id, tracked_at',
 *   getEntityId: (item) => item.committee_id,
 *   entityName: 'committee',
 * });
 */
export function createTrackingHook<
  TItem extends BaseTrackedItem,
  TEntityId extends number | string = number,
>(config: TrackingConfig<TItem, TEntityId>) {
  const {
    tableName,
    queryKey,
    entityIdField,
    tierLimitKey,
    selectColumns,
    getEntityId,
    entityName,
  } = config;

  const QUERY_KEY = [queryKey] as const;

  // Fetch function
  async function fetchTrackedItems(userId: string): Promise<TItem[]> {
    const { data, error } = await supabase
      .from(tableName)
      .select(selectColumns)
      .eq('user_id', userId)
      .order('tracked_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch tracked ${entityName}s: ${error.message}`);
    }

    return (data as TItem[]) || [];
  }

  // Track function
  async function trackItem(userId: string, entityId: TEntityId): Promise<TItem> {
    const { data, error } = await supabase
      .from(tableName)
      .insert({
        user_id: userId,
        [entityIdField]: entityId,
      })
      .select()
      .single();

    if (error) {
      if (error.code === POSTGRES_UNIQUE_VIOLATION) {
        throw new Error(`You are already tracking this ${entityName}`);
      }
      throw new Error(`Failed to track ${entityName}: ${error.message}`);
    }

    return data as TItem;
  }

  // Untrack function
  async function untrackItem(userId: string, entityId: TEntityId): Promise<void> {
    const { error } = await supabase
      .from(tableName)
      .delete()
      .eq('user_id', userId)
      .eq(entityIdField, entityId);

    if (error) {
      throw new Error(`Failed to untrack ${entityName}: ${error.message}`);
    }
  }

  // The actual hook
  return function useTracking(): TrackingHookResult<TItem, TEntityId> {
    const { user, tierLimits } = useAuth();
    const queryClient = useQueryClient();
    const userId = user?.id;

    // Query to fetch tracked items
    const trackedQuery = useQuery({
      queryKey: [...QUERY_KEY, userId],
      queryFn: () => fetchTrackedItems(userId!),
      ...standardQueryDefaults,
      enabled: !!userId,
    });

    // Derive a Set of tracked entity IDs for O(1) lookup
    const trackedIds = useMemo(
      () => new Set(trackedQuery.data?.map(getEntityId) ?? []),
      [trackedQuery.data]
    );

    // Tier-based tracking limits
    const trackedCount = trackedIds.size;
    const limit = tierLimits[tierLimitKey];

    const canTrackMore = useMemo(() => {
      if (limit === null) return true;
      return trackedCount < limit;
    }, [trackedCount, limit]);

    const remainingSlots = useMemo(() => {
      if (limit === null) return null;
      return Math.max(0, limit - trackedCount);
    }, [trackedCount, limit]);

    // Helper to check if an entity is tracked
    const isTracked = (entityId: TEntityId): boolean => trackedIds.has(entityId);

    // Track mutation with optimistic update
    const trackMutation = useMutation({
      mutationFn: (entityId: TEntityId) => trackItem(userId!, entityId),
      onMutate: async (entityId) => {
        // Cancel outgoing refetches to prevent overwriting optimistic update
        await queryClient.cancelQueries({ queryKey: [...QUERY_KEY, userId] });

        // Snapshot current state for rollback on error
        const previousItems = queryClient.getQueryData<TItem[]>([...QUERY_KEY, userId]);

        // Create optimistic item
        const optimisticItem = {
          id: `temp-${entityId}`,
          user_id: userId!,
          [entityIdField]: entityId,
          tracked_at: new Date().toISOString(),
        } as TItem;

        queryClient.setQueryData<TItem[]>([...QUERY_KEY, userId], (old) => [
          optimisticItem,
          ...(old ?? []),
        ]);

        return { previousItems };
      },
      onError: (_err, _entityId, context) => {
        if (context?.previousItems) {
          queryClient.setQueryData([...QUERY_KEY, userId], context.previousItems);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, userId] });
      },
    });

    // Untrack mutation with optimistic update
    const untrackMutation = useMutation({
      mutationFn: (entityId: TEntityId) => untrackItem(userId!, entityId),
      onMutate: async (entityId) => {
        // Cancel outgoing refetches to prevent overwriting optimistic update
        await queryClient.cancelQueries({ queryKey: [...QUERY_KEY, userId] });

        // Snapshot current state for rollback on error
        const previousItems = queryClient.getQueryData<TItem[]>([...QUERY_KEY, userId]);

        queryClient.setQueryData<TItem[]>(
          [...QUERY_KEY, userId],
          (old) => old?.filter((item) => getEntityId(item) !== entityId) ?? []
        );

        return { previousItems };
      },
      onError: (_err, _entityId, context) => {
        if (context?.previousItems) {
          queryClient.setQueryData([...QUERY_KEY, userId], context.previousItems);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, userId] });
      },
    });

    // Toggle function
    const toggleTracking = async (entityId: TEntityId): Promise<void> => {
      if (isTracked(entityId)) {
        await untrackMutation.mutateAsync(entityId);
      } else {
        await trackMutation.mutateAsync(entityId);
      }
    };

    return {
      trackedItems: trackedQuery.data ?? [],
      trackedIds,
      trackedCount,
      limit,
      canTrackMore,
      remainingSlots,
      isLoading: trackedQuery.isLoading,
      isError: trackedQuery.isError,
      error: trackedQuery.error,
      isTracked,
      trackMutation,
      untrackMutation,
      toggleTracking,
      isToggling: trackMutation.isPending || untrackMutation.isPending,
      refetch: trackedQuery.refetch,
    };
  };
}

/**
 * Create a lightweight hook that only returns tracked IDs.
 * Useful for components that only need to check if an entity is tracked.
 */
export function createTrackedIdsHook<
  TItem extends BaseTrackedItem,
  TEntityId extends number | string = number,
>(
  config: Pick<
    TrackingConfig<TItem, TEntityId>,
    'tableName' | 'queryKey' | 'selectColumns' | 'getEntityId' | 'entityName'
  >
) {
  const { tableName, queryKey, selectColumns, getEntityId, entityName } = config;
  const QUERY_KEY = [queryKey] as const;

  async function fetchTrackedItems(userId: string): Promise<TItem[]> {
    const { data, error } = await supabase
      .from(tableName)
      .select(selectColumns)
      .eq('user_id', userId)
      .order('tracked_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch tracked ${entityName}s: ${error.message}`);
    }

    return (data as TItem[]) || [];
  }

  return function useTrackedIds() {
    const { user } = useAuth();
    const userId = user?.id;

    const { data, isLoading } = useQuery({
      queryKey: [...QUERY_KEY, userId],
      queryFn: () => fetchTrackedItems(userId!),
      ...standardQueryDefaults,
      enabled: !!userId,
      select: (items) => new Set(items.map(getEntityId)),
    });

    return {
      trackedIds: data ?? new Set<TEntityId>(),
      isLoading,
      isTracked: (entityId: TEntityId) => data?.has(entityId) ?? false,
    };
  };
}
